/**
 * Hydration-mismatch enrichment for Bugsnag.
 *
 * React ships hydration failures minified ("Minified React error #418") with no
 * useful payload: `args[]=HTML` names the element kind and nothing else — not
 * the route, not which client-only state diverged, not whether a browser
 * extension injected the node. Every triage of BugSnag 69eb8cfaba7b40640095ddd5
 * stalled on exactly that. This module collects the small, non-identifying
 * context that separates those causes, and config.ts attaches it to the report.
 *
 * PRIVACY — checkout sessionStorage holds personal data (address form fields,
 * contact e-mail, per-step payloads). Only the KEY NAMES are ever reported, plus
 * the value of two keys whose contents are closed sets:
 *
 *   - `checkout-fulfilment-mode` → "pickup" | "delivery" | null, nothing else
 *     (anything unrecognised is reported as "other", never echoed);
 *   - `checkout-progress`        → the numeric currentStep only.
 *
 * `checkout-step-data` and `checkout-address-form` (the two that DO hold
 * personal data) are only ever reported as present/absent. Do not widen this
 * list without re-reading what each key stores (useSessionState.ts,
 * CheckoutProgressContext.tsx, AddressStep.tsx).
 *
 * Browser-only: every accessor is guarded and the collector returns null on the
 * server, so importing this from the shared config is inert in Node.
 */

/** Every checkout key written by useSessionState / AddressStep / CheckoutStepper. */
const CHECKOUT_SESSION_KEYS = [
  "checkout-progress",
  "checkout-step-data",
  "checkout-address-form",
  "checkout-fulfilment-mode",
  "checkout-cart-fingerprint",
  "checkout-session-validated",
] as const

/** Cap on reported child tag names — a report, not a DOM dump. */
const MAX_CHILD_TAGS = 30

export interface HydrationContext {
  /** Route the mismatch happened on (no query string — it can carry tokens). */
  pathname: string
  /** Which checkout keys existed, by NAME only. */
  checkoutKeys: string[]
  /** "pickup" | "delivery" | "other" | null — the client-only state behind #418. */
  fulfilmentMode: string | null
  /** Persisted checkout step number, or null when absent/unparsable. */
  checkoutStep: number | null
  /** Tag names of <body>'s direct children — exposes extension-injected nodes. */
  bodyChildren: string[]
  /** Tag names under #__next, or null when that element does not exist. */
  nextRootChildren: string[] | null
  /** <html lang> — a translation extension rewriting it is a known #418 source. */
  documentLang: string | null
}

/** True for the two wordings React uses for a hydration mismatch (minified and dev). */
export function isHydrationMismatchError(errorMessage?: string): boolean {
  if (!errorMessage) return false
  return /Minified React error #418|Hydration failed/.test(errorMessage)
}

function readSessionItem(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key)
  } catch {
    // sessionStorage disabled by browser policy — treat as absent.
    return null
  }
}

/** Closed-set read: never echoes an unexpected stored value. */
function readFulfilmentMode(): string | null {
  const raw = readSessionItem("checkout-fulfilment-mode")
  if (raw === null) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (parsed === "pickup" || parsed === "delivery") return parsed
    if (parsed === null) return null
  } catch {
    // corrupted JSON — fall through to "other".
  }
  return "other"
}

/** Numeric read: only currentStep, and only when it really is a number. */
function readCheckoutStep(): number | null {
  const raw = readSessionItem("checkout-progress")
  if (raw === null) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (parsed && typeof parsed === "object" && "currentStep" in parsed) {
      const step = (parsed as { currentStep: unknown }).currentStep
      if (typeof step === "number" && Number.isFinite(step)) return step
    }
  } catch {
    // corrupted JSON — report as unknown.
  }
  return null
}

function childTagNames(parent: Element | null): string[] | null {
  if (!parent) return null
  return Array.from(parent.children)
    .slice(0, MAX_CHILD_TAGS)
    .map((el) => el.tagName.toLowerCase())
}

/**
 * Collects the hydration context, or null when there is nothing to collect
 * (server runtime, or the DOM is unreachable). Never throws.
 */
export function collectHydrationContext(): HydrationContext | null {
  if (typeof window === "undefined" || typeof document === "undefined") return null

  try {
    const checkoutKeys = CHECKOUT_SESSION_KEYS.filter((key) => readSessionItem(key) !== null)

    return {
      pathname: window.location?.pathname ?? "",
      checkoutKeys: [...checkoutKeys],
      fulfilmentMode: readFulfilmentMode(),
      checkoutStep: readCheckoutStep(),
      bodyChildren: childTagNames(document.body) ?? [],
      nextRootChildren: childTagNames(document.getElementById("__next")),
      documentLang: document.documentElement?.getAttribute("lang") ?? null,
    }
  } catch {
    // Enrichment must never break the report it is attached to.
    return null
  }
}
